import React, { useState, useEffect } from "react";
import {
  Users,
  ShoppingBag,
  Package,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  CalendarDays,
  BarChart3,
  Clock,
  AlertCircle,
  CheckCircle,
  Tag,
  MessageCircle,
  Bell,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  XCircle,
  PlusCircle,
} from "lucide-react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import axios from "axios";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const StatusBadge = ({ status }) => {
  let color, bgColor, Icon;

  switch (status) {
    case "Livré":
      color = "text-green-700";
      bgColor = "bg-green-100";
      Icon = CheckCircle;
      break;
    case "En cours":
      color = "text-yellow-700";
      bgColor = "bg-yellow-100";
      Icon = Clock;
      break;
    case "Livré au livreur":
      color = "text-blue-700";
      bgColor = "bg-blue-100";
      Icon = Package;
      break;
    case "Annulé":
      color = "text-red-700";
      bgColor = "bg-red-100";
      Icon = XCircle;
      break;
    default:
      color = "text-gray-700";
      bgColor = "bg-gray-100";
      Icon = AlertTriangle;
  }

  return (
    <div
      className={`flex items-center px-2 py-1 rounded-full ${bgColor} ${color} text-xs`}
    >
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </div>
  );
};

const StatCard = ({ title, value, icon, color, percentChange, subtitle }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 flex flex-col">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="text-sm font-medium text-gray-500">{title}</div>
          <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
        <div
          className="p-2 rounded-full"
          style={{ backgroundColor: `${color}20` }}
        >
          {React.cloneElement(icon, { className: "w-6 h-6", style: { color } })}
        </div>
      </div>

      {percentChange !== undefined && (
        <div className="flex items-center text-sm mt-2">
          {percentChange >= 0 ? (
            <ArrowUp className="w-4 h-4 mr-1 text-green-500" />
          ) : (
            <ArrowDown className="w-4 h-4 mr-1 text-red-500" />
          )}
          <span
            className={percentChange >= 0 ? "text-green-500" : "text-red-500"}
          >
            {Math.abs(percentChange)}%
          </span>
          <span className="text-gray-500 ml-1">{subtitle}</span>
        </div>
      )}
    </div>
  );
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

export default function Overview() {
  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
  const [allUsers, setAllUsers] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allAddress, setAllAddress] = useState([]);
  const [allCommandes, setAllCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [allcates, setAllCate] = useState([]);

  // États pour les statistiques et graphiques dynamiques
  const [salesData, setSalesData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockProducts: 0,
    orderGrowth: 0,
    revenueGrowth: 0,
    customerGrowth: 0,
  });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getChartWidth = () => {
    if (windowWidth < 640) return "100%"; // Mobile
    if (windowWidth < 1024) return "100%"; // Tablet
    return "100%"; // Desktop
  };

  // Sample data transformation based on your provided data structure
  const transformOrdersToSalesData = (orders) => {
    console.log(orders);
    // Group sales by date
    const salesByDate = orders.reduce((acc, order) => {
      const date = new Date(order.date);
      const dateStr = date.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
      });

      if (!acc[dateStr]) {
        acc[dateStr] = 0;
      }

      // Add the order total to the accumulated amount
      acc[dateStr] += order.prixTotal || 0;

      return acc;
    }, {});

    // Convert to array format needed for chart
    return Object.keys(salesByDate)?.map((date) => ({
      name: date,
      montant: salesByDate[date],
    }));
  };

  // Fonction pour formater la monnaie
  // Format currency function
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Fonction pour obtenir les statistiques de commandes par statut
  const getOrderStatusStats = (orders) => {
    const statusCount = {};

    orders.forEach((order) => {
      const status = order.statusLivraison || "Inconnu";
      statusCount[status] = (statusCount[status] || 0) + 1;
    });

    const statusColors = {
      "En cours": "#FFB500",
      "Livré au livreur": COLORS.orange,
      "En cours de livraison": "#3B82F6",
      livré: COLORS.green,
      annulé: "#EF4444",
      Inconnu: COLORS.gray,
    };

    return Object.entries(statusCount)?.map(([name, value]) => ({
      name,
      value,
      color: statusColors[name] || COLORS.gray,
    }));
  };

  // Fonction pour obtenir les ventes mensuelles
  const getMonthlySales = (orders) => {
    const monthNames = [
      "Jan",
      "Fév",
      "Mar",
      "Avr",
      "Mai",
      "Jui",
      "Juil",
      "Août",
      "Sep",
      "Oct",
      "Nov",
      "Déc",
    ];
    const monthlySales = Array(12)
      .fill(0)
      ?.map((_, i) => ({ name: monthNames[i], montant: 0 }));

    orders.forEach((order) => {
      if (order.date) {
        const date = new Date(order.date);
        const month = date.getMonth();
        monthlySales[month].montant += order.prixTotal || 0;
      }
    });

    // Retourner les 7 derniers mois avec des données
    const nonZeroMonths = monthlySales.filter((month) => month.montant > 0);
    return nonZeroMonths.length > 0
      ? nonZeroMonths.slice(-7)
      : monthlySales.slice(0, 7);
  };

  // Fonction pour obtenir les ventes par catégorie
  const getCategorySales = (products, orders, categories) => {
    // Créer un mapping des catégories par ID
    const categoryMap = {};
    categories.forEach((category) => {
      categoryMap[category._id] = category.name;
    });

    // Créer un mapping des produits et leurs catégories
    const productCategoryMap = {};
    products.forEach((product) => {
      if (product.ClefCategorie) {
        productCategoryMap[product._id] = product.ClefCategorie;
      }
    });

    // Initialiser les compteurs de ventes par catégorie
    const categorySales = {};

    // Parcourir toutes les commandes
    orders.forEach((order) => {
      if (order.nbrProduits && Array.isArray(order.nbrProduits)) {
        order.nbrProduits.forEach((item) => {
          const productId = item.produit;
          const quantity = item.quantite || 1;

          // Trouver la catégorie du produit
          const categoryId = productCategoryMap[productId];

          if (categoryId) {
            if (!categorySales[categoryId]) {
              categorySales[categoryId] = {
                count: 0,
                amount: 0,
              };
            }

            categorySales[categoryId].count += quantity;

            // Calculer le montant approximatif pour cette ligne de commande
            // Si nous avons un prix total pour la commande, répartir proportionnellement
            if (order.prixTotal && order.nbrProduits.length > 0) {
              // Estimation simplifiée: répartition égale entre les produits
              categorySales[categoryId].amount +=
                (order.prixTotal / order.nbrProduits.length) * quantity;
            }
          }
        });
      }
    });

    // Convertir les données en format pour graphique

    // Données basées sur le nombre de produits vendus
    const categoryCountData = Object.entries(categorySales)?.map(
      ([categoryId, data]) => ({
        name: categoryMap[categoryId] || "Catégorie inconnue",
        value: data.count,
      })
    );

    // Données basées sur le montant des ventes
    const categoryAmountData = Object.entries(categorySales)?.map(
      ([categoryId, data]) => ({
        name: categoryMap[categoryId] || "Catégorie inconnue",
        value: Math.round(data.amount),
      })
    );

    return {
      byCount: categoryCountData,
      byAmount: categoryAmountData,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [commandes, users, profiles, addresses, product, messages, cat] =
          await Promise.all([
            axios.get(`${BackendUrl}/api/ordersRoutes/`),
            axios.get(`${BackendUrl}/api/user/getUsers`),
            axios.get(`${BackendUrl}/api/profilesRoutes/all`),
            axios.get(`${BackendUrl}/api/shippingAddressRoutes/all`),
            axios.get(`${BackendUrl}/api/productsRoutes`),
            axios.get(`${BackendUrl}/api/messagesRoutes`),
            axios.get(`${BackendUrl}/api/categoriesRoutes`),
          ]);

        const commandesData = commandes.data.commandes || [];
        const usersData = users.data?.user || [];
        const profilesData = profiles.data.data || [];
        const productsData = product.data.data || [];
        const messagesData = messages.data || [];
        const catesData = cat.data.data || [];

        setAllCommandes(commandesData);
        setAllUsers(usersData);
        setAllProfiles(profilesData);
        setAllAddress(addresses.data.data || []);
        setProducts(productsData);
        setAllMessages(messagesData);
        setAllCate(catesData);
        // console.log(catesData);

        // Calculer les statistiques
        const totalOrders = commandesData.length;
        const totalRevenue = commandesData.reduce(
          (sum, order) => sum + (order.prixTotal || 0),
          0
        );
        // console.log(commandesData);
        const totalCustomers = usersData.length;
        const totalProducts = productsData.length;
        const lowStockProducts = productsData.filter(
          (p) => (p.countInStock || 0) < 10
        ).length;

        // Supposons que nous avons une croissance de 10% pour la démonstration
        // Dans un cas réel, vous pourriez comparer avec les mois précédents
        setStats({
          totalOrders,
          totalRevenue,
          totalCustomers,
          totalProducts,
          lowStockProducts,
          orderGrowth: 10,
          revenueGrowth: 8.3,
          customerGrowth: 5.2,
        });

        // Préparer les données pour les graphiques
        setSalesData(getMonthlySales(commandesData));
        setOrderStatusData(getOrderStatusStats(commandesData));
        setCategoryData(
          getCategorySales(productsData, commandesData, catesData)
        );
        console.log(getCategorySales(productsData, commandesData, catesData));

        // Préparer les commandes récentes
        const latestOrders = [...commandesData]
          .sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
          )
          .slice(0, 4)
          ?.map((order) => {
            const user = usersData.find((u) => u._id === order.user);
            const userName = user ? user.name || user.email : "Client inconnu";

            return {
              id:
                order._id?.substring(0, 8) ||
                `CMD-${Math.random().toString().substring(2, 6)}`,
              client: userName,
              date: new Date(order.createdAt || new Date()).toLocaleDateString(
                "fr-FR"
              ),
              prix: order.prixTotal || 0,
              status: order.statusLivraison || "En cours",
            };
          });
        setRecentOrders(latestOrders);

        // Préparer les messages récents
        const latestMessages = [...messagesData]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
          .slice(0, 3)
          ?.map((msg) => {
            const user = usersData.find((u) => u._id === msg.clefUser);
            const userName = user
              ? user.name || user.email
              : "Utilisateur inconnu";

            return {
              id: msg._id,
              user: userName,
              message: msg.message || "Message vide",
              date: new Date(msg.date || new Date()).toLocaleDateString(
                "fr-FR"
              ),
              read: msg.read || false,
            };
          });
        setRecentMessages(latestMessages);

        // Préparer les clients récents
        const latestCustomers = [...usersData]
          .sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
          )
          .slice(0, 3)
          ?.map((user) => {
            const userOrders = commandesData.filter(
              (o) => o.clefUser === user._id
            );

            return {
              id: user._id,
              name: user.name || user.email,
              email: user.email,
              date: new Date(user.createdAt || new Date()).toLocaleDateString(
                "fr-FR"
              ),
              orders: userOrders.length,
            };
          });
        setRecentCustomers(latestCustomers);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        <span className="ml-2">Chargement des données...</span>
      </div>
    );
  }

  // console.log(salesData);
  // const salesData2 = transformOrdersToSalesData(salesData);
  // console.log(salesData2);
  return (
    <div className="flex flex-col space-y-6">
      {/* En-tête avec logo et titre */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: COLORS.green }}
          >
            <span className="text-xl font-bold text-white">K</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: COLORS.green }}>
              Tableau de Bord Kassarmou
            </h1>
            <p className="text-gray-500">
              Bienvenue sur votre espace d'administration
            </p>
          </div>
        </div>
        <div className="hidden md:block text-right">
          <div className="text-sm text-gray-500">Aujourd'hui</div>
          <div className="font-medium">
            {new Date().toLocaleDateString("fr-FR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        </div>
      </div>

      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Commandes Totales"
          value={stats.totalOrders.toString()}
          icon={<ShoppingCart />}
          color={COLORS.green}
          percentChange={stats.orderGrowth}
          subtitle="vs. mois précédent"
        />
        <StatCard
          title="Chiffre d'Affaires"
          value={formatCurrency(stats.totalRevenue)}
          icon={<DollarSign />}
          color={COLORS.orange}
          percentChange={stats.revenueGrowth}
          subtitle="vs. mois précédent"
        />
        <StatCard
          title="Clients"
          value={stats.totalCustomers.toString()}
          icon={<Users />}
          color="#3B82F6"
          percentChange={stats.customerGrowth}
          subtitle="vs. mois précédent"
        />
        <StatCard
          title="Produits"
          value={stats.totalProducts.toString()}
          icon={<Package />}
          color="#8B5CF6"
          subtitle={`${stats.lowStockProducts} en stock faible`}
        />
      </div>

      {/* Graphiques de ventes et d'ordres */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique de ventes */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.green }}>
              Ventes Mensuelles
            </h2>
            <div className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
              Derniers mois
            </div>
          </div>
          {salesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.lightGray}
                />
                <XAxis dataKey="name" stroke="#718096" />
                <YAxis stroke="#718096" />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Montant"]}
                />
                <Line
                  type="monotone"
                  dataKey="montant"
                  stroke={COLORS.green}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Aucune donnée de vente disponible
            </div>
          )}
        </div>

        {/* Graphique de statut des commandes */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.orange }}>
              Statut des Commandes
            </h2>
            <div className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded">
              Toutes les commandes
            </div>
          </div>
          {orderStatusData.length > 0 ? (
            <div className="flex flex-col md:flex-row items-center justify-around">
              <ResponsiveContainer width={getChartWidth()} height={230}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {orderStatusData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    wrapperStyle={{ fontSize: "12px" }}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Aucune donnée de commande disponible
            </div>
          )}
        </div>
      </div>

      {/* Troisième rangée */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Commandes récentes */}
        <div className="col-span-2 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.green }}>
              Commandes Récentes
            </h2>
            <div className="text-sm">
              <a
                href="/Admin/AOrders"
                className="text-blue-600 hover:underline"
              >
                Voir tout
              </a>
            </div>
          </div>
          {recentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders?.map((order, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium">
                        {order.id}
                      </td>
                      <td className="py-3 px-4 text-sm">{order.client}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {order.date}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {order.prix.toLocaleString()} €
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={order.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex justify-center items-center h-32 text-gray-500">
              Aucune commande récente disponible
            </div>
          )}
        </div>

        {/* Messages récents */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.orange }}>
              Messages Récents
            </h2>
            <div className="text-sm">
              <a href="/Admin/Imbox" className="text-blue-600 hover:underline">
                Voir tout
              </a>
            </div>
          </div>
          {recentMessages.length > 0 ? (
            <div className="space-y-3">
              {recentMessages?.map((msg, index) => {
                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg ${
                      msg.read ? "bg-gray-50" : "bg-orange-50"
                    } flex items-start`}
                  >
                    <div className="flex-shrink-0 mr-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="font-medium text-gray-600">
                          {msg.user[0]}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">{msg.user}</span>
                        <span className="text-xs text-gray-500">
                          {msg.date}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {msg.message}
                      </p>
                    </div>
                    {!msg.read && (
                      <div className="flex-shrink-0 ml-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex justify-center items-center h-32 text-gray-500">
              Aucun message récent disponible
            </div>
          )}
        </div>
      </div>

      {/* Quatrième rangée */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique de catégories */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.green }}>
              Ventes par Catégorie
            </h2>
          </div>
          {categoryData.byAmount && categoryData.byAmount.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData.byAmount}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.lightGray}
                />
                <XAxis dataKey="name" stroke="#718096" />
                <YAxis stroke="#718096" />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill={COLORS.green}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Aucune donnée de catégorie disponible
            </div>
          )}
        </div>

        {/* Clients récents */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg" style={{ color: COLORS.orange }}>
              Nouveaux Clients
            </h2>
            <div className="text-sm">
              <a
                href="/Admin/ACustomers"
                className="text-blue-600 hover:underline"
              >
                Voir tout
              </a>
            </div>
          </div>
          {recentCustomers.length > 0 ? (
            <div className="space-y-4">
              {recentCustomers?.map((customer, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border-b last:border-0"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mr-3">
                      <span className="font-semibold">
                        {customer.name[0]}
                        {customer.name.split(" ")[1]?.[0]}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">{customer.name}</div>
                      <div className="text-sm text-gray-500">
                        {customer.email}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      Inscrit le {customer.date}
                    </div>
                    <div
                      className="text-sm font-medium"
                      style={{ color: COLORS.green }}
                    >
                      {customer.orders} commandes
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center h-32 text-gray-500">
              Aucun client récent disponible
            </div>
          )}
        </div>
      </div>

      {/* Footer avec actions rapides */}
      <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/Admin/AddProductA"
            className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-green-50 transition-colors"
          >
            <div
              className="p-2 rounded-full mb-2"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <PlusCircle style={{ color: COLORS.green }} className="w-6 h-6" />
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: COLORS.green }}
            >
              Ajouter Produit
            </span>
          </a>

          <a
            href="/Admin/AOrders"
            className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-green-50 transition-colors"
          >
            <div
              className="p-2 rounded-full mb-2"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <ShoppingCart
                style={{ color: COLORS.green }}
                className="w-6 h-6"
              />
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: COLORS.green }}
            >
              Gérer Commandes
            </span>
          </a>

          <a
            href="/Admin/Imbox"
            className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-green-50 transition-colors"
          >
            <div
              className="p-2 rounded-full mb-2"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <MessageCircle
                style={{ color: COLORS.green }}
                className="w-6 h-6"
              />
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: COLORS.green }}
            >
              Messages
            </span>
          </a>

          {/* <a
            href="/Admin/Analytics"
            className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-green-50 transition-colors"
          >
            <div
              className="p-2 rounded-full mb-2"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <BarChart3 style={{ color: COLORS.green }} className="w-6 h-6" />
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: COLORS.green }}
            >
              Analytiques
            </span>
          </a> */}
        </div>
      </div>
    </div>
  );
}
